import styleConfig from '@config/style.json';
import type { Theme, ResponsiveConfig, ChartThemeColors } from '@/types';

let cachedTheme: Theme | null = null;

/**
 * Colour for text links inside the detail views: the host page's
 * `--gc-link` when it sets one, otherwise the AIRI site's own link red
 * (`--primary`, #a32035) rather than the browser-ish blue.
 */
export const TEXT_LINK_COLOR = 'var(--gc-link, #a32035)';

export function loadTheme(): Theme {
  if (cachedTheme) return cachedTheme;
  cachedTheme = styleConfig as Theme;
  return cachedTheme;
}

export function getResponsiveConfig(width: number): ResponsiveConfig {
  const theme = loadTheme();
  const { breakpoints } = theme.responsive;

  if (width < breakpoints.mobile) {
    return theme.responsive.mobile;
  } else if (width < breakpoints.tablet) {
    return theme.responsive.tablet;
  }
  return theme.responsive.desktop;
}

export function isMobileWidth(width: number): boolean {
  const theme = loadTheme();
  return width < theme.responsive.breakpoints.mobile;
}

export function detectDarkMode(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function getChartTheme(isDark: boolean): ChartThemeColors {
  const theme = loadTheme();
  return isDark ? theme.colors.theme.dark : theme.colors.theme.light;
}

export function getCoverageColor(coverage: string): string {
  const theme = loadTheme();
  const map: Record<string, string> = {
    'Good coverage': theme.colors.coverage.good,
    'Minimal coverage': theme.colors.coverage.minimal,
    'No coverage': theme.colors.coverage.none,
  };
  return map[coverage] ?? theme.colors.category[0];
}

export function getCategoryColor(index: number): string {
  const theme = loadTheme();
  return theme.colors.category[index % theme.colors.category.length];
}

export function getActorRoleColor(role: string): string {
  const theme = loadTheme();
  return theme.colors.actorRole[role] ?? getCategoryColor(0);
}
